import { createAction } from '@reduxjs/toolkit'
import axios from 'axios'
import {
  ChannelTypePerson,
  Message,
} from 'wukongimjssdk'

import { AIChatPolicies } from '@/constants/ai-chat-policy'
import { Conversation } from '@/model/conversation'
import { MessageContent } from '@/model/message'
import { createAppAsyncThunk } from '@/redux/utils'
import { getIMToken } from '@/utils/api'
import { waitForSDK } from '@/utils/bae-im-sdk'

import { AIChatChoice } from './message-actions'

export const conversationNamespace = 'conversation'

export interface SuggestedChatResult {
  suggestionFetching: boolean
  choices: AIChatChoice[]
  policyAction?: AIChatPolicies | undefined
}

export interface ConversationQuery {
  query: string
  conversationIds: number[]
}

export const DEFAULT_QUERY: ConversationQuery = {
  query: '',
  conversationIds: [],
}

export interface ReadAllConversationsParams {
  creatorId: number
  creatorUserId: number
}

const parseLastMessageContent = (lastMessage: Message | undefined) => {
  if (!lastMessage) return undefined
  try {
    return JSON.parse(lastMessage.content.text) as MessageContent
  } catch (error) {
    console.log(error)
    return undefined
  }
}

export const readAllConversations = createAppAsyncThunk<Conversation[], ReadAllConversationsParams>(
  `${conversationNamespace}/readAllConversations`,
  async ({ creatorUserId }: ReadAllConversationsParams) => {
    const sdk = await waitForSDK({ userId: creatorUserId })
    if (sdk) {
      const conversations = await sdk.getAllConversation()
      return conversations.map(({ channel, lastMessage, timestamp, unread }) => ({
        id: Number(channel.channelID),
        lastMessage: parseLastMessageContent(lastMessage),
        timestamp: lastMessage?.timestamp || timestamp,
        unread,
      }))
    }
    return []
  },
)

export interface CreateConversationParams {
  creatorId: number
  creatorUserId: number
  conversationId: number
}

export const createConversation = createAppAsyncThunk(
  `${conversationNamespace}/createConversation`,
  async ({
    creatorUserId,
    conversationId,
  }: CreateConversationParams) => {
    const sdk = await waitForSDK({ userId: creatorUserId })
    if (sdk) {
      const conversation = await sdk.createEmptyConversation(conversationId.toString())
      return {
        id: conversationId,
        lastMessage: conversation.lastMessage?.content,
        timestamp: conversation.timestamp,
        unread: 0,
      }
    }
  },
)

export interface RemoveConversationParams {
  creatorId: number
  creatorUserId: number
  conversationId: number
}

export const removeConversation = createAppAsyncThunk(
  `${conversationNamespace}/removeConversation`,
  async ({
    creatorUserId,
    conversationId,
  }: RemoveConversationParams) => {
    const sdk = await waitForSDK({ userId: creatorUserId })
    if (sdk) {
      await axios.post('https://c.bae.boo/conversations/delete', {
        uid: creatorUserId.toString(),
        channel_id: conversationId.toString(),
        channel_type: ChannelTypePerson,
      }, {
        headers: { token: getIMToken() },
      })
      sdk.removeConversation(conversationId.toString())
    }
  },
)

export interface ClearConversationParams {
  creatorId: number
  creatorUserId: number
  conversationId: number
}

export const clearConversationUnread = createAppAsyncThunk(
  `${conversationNamespace}/clearConversationUnread`,
  async ({
    creatorUserId,
    conversationId,
  }: ClearConversationParams) => {
    const sdk = await waitForSDK({ userId: creatorUserId })
    if (sdk) {
      sdk.clearConversationUnread(conversationId.toString())
    }
  },
)

export const updateConversationQuery = createAction<ConversationQuery & { creatorId: number }>(
  `${conversationNamespace}/updateConversationQuery`,
)
